/**
 * templateEditsStore.ts
 * Persists template edits (layout edits + overlays) independently of the
 * Excel/template pair, so reopening the app restores the last template
 * customization without re-doing it.
 *
 * Unlike `sessionStore` (keyed by excelHash+templateHash), this uses a fixed
 * key. The template hash is stored alongside so a different template can be
 * flagged instead of having its edits land on the wrong document.
 */

import type { TemplateLayoutEdit } from "@/utils/templateLayoutEdits";
import type { TemplateOverlay } from "@/utils/templateOverlays";

const STORAGE_KEY = "tvs-excel-uploader:template-edits";

export interface TemplateEdits {
  version: 1;
  savedAt: number;
  /** Template hash the edits were made against. */
  templateHash: string;
  layoutEdits: TemplateLayoutEdit[];
  overlays: TemplateOverlay[];
}

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function defaultStorage(): StorageLike | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function saveTemplateEdits(
  templateHash: string,
  layoutEdits: TemplateLayoutEdit[],
  overlays: TemplateOverlay[],
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage || !templateHash) return;
  try {
    const payload: TemplateEdits = {
      version: 1,
      savedAt: Date.now(),
      templateHash,
      layoutEdits,
      overlays,
    };
    storage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota / private-mode failures must never break the flow.
  }
}

export function loadTemplateEdits(
  storage: StorageLike | null = defaultStorage()
): TemplateEdits | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TemplateEdits;
    if (!parsed || parsed.version !== 1) return null;
    if (!Array.isArray(parsed.layoutEdits) || !Array.isArray(parsed.overlays)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearTemplateEdits(
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
